import { nextTick, onMounted, watch } from 'vue';
import type { EmitType } from '@mts-fe/types';
import type { ComputedRef } from 'vue';
import { findAttributeInParents, isElementHidden, onElementVisible } from '@mts-fe/utils';
import { inMtsApp, mtsEmitter } from '@mts/utils';
import type { FormProps, SetFieldsValueOptions } from '../types/form';

export interface FormFocusReturn {
  /** enter键事件处理器 */
  handleKeyboardEnter: (event: KeyboardEvent) => void;
  /** 根据表单字段名手动聚焦控件 */
  focusByField: (field: string) => void;
  /** 计算表单中可置顶的输入框的实际宽度 */
  updateFocusFixedInput: () => void;
}
interface FocusOptions {
  autoSelect?: boolean;
}

const SELECTOR = ['input', 'textarea', 'select', '.ant-switch']
  .map((i) => '.ant-form-item ' + i)
  .join(',');

/**
 * 表单回车与扫描聚焦
 */
export function useFormFocus({
  getFormEl,
  getProps,
  handleSubmit,
  setFieldsValue,
  emit,
}: {
  getFormEl: () => HTMLDivElement | undefined;
  getProps: ComputedRef<FormProps>;
  handleSubmit: (e?: Event) => Promise<void>;
  setFieldsValue: (values: Recordable, opt?: SetFieldsValueOptions) => Promise<void>;
  emit: EmitType;
}): FormFocusReturn {
  let focusableElements: HTMLElement[] = [];

  const collectFocusableElements = (): void => {
    const formEl = getFormEl();
    if (!formEl) return;

    const elements = Array.from(formEl.querySelectorAll(SELECTOR)) as HTMLElement[];
    focusableElements = filterUnFocusableElements(elements);
  };

  /** enter键事件处理器 */
  const handleKeyboardEnter = (event: KeyboardEvent): void => {
    if (event.key !== 'Enter') return;
    if (!getProps.value.enterFocus) {
      if (getProps.value.autoSubmitOnEnter && event.target instanceof HTMLElement) {
        event.target?.tagName.toUpperCase() === 'INPUT' && handleSubmit(event);
      }
      return;
    }

    event.preventDefault();

    nextTick(() => focusNextByElement(event.target as HTMLElement));
  };

  /** 扫码后自动赋值并聚焦下一个元素 */
  const handleScan = async ({ code }) => {
    if (!document.activeElement) return;

    const element = document.activeElement as HTMLElement;
    !focusableElements.length && collectFocusableElements();
    const field = findAttributeInParents(element, 'data-scan', {
      stopAtClasses: ['ant-form-item'],
    });
    if (!field) return;

    setFieldsValue({ [field]: code });
    nextTick(() => focusNextByElement(element));
  };

  onMounted(() => {
    watch(
      () => getProps.value.scanFocusNext,
      (value, oldValue) => {
        if (!value) {
          oldValue && mtsEmitter.off('scannerReadCode', handleScan);
          return;
        }
        mtsEmitter.on('scannerReadCode', handleScan);
      },
    );
  });

  const focusNextByElement = (element: HTMLElement, options: FocusOptions = {}) => {
    !focusableElements.includes(element) && collectFocusableElements();

    const currentIndex = focusableElements.indexOf(element);
    if (currentIndex === -1) {
      return console.warn('[useFormFocus] Element is not focusable');
    }
    if (currentIndex >= focusableElements.length - 1) {
      emit('focus-change', false);
      return;
    }

    focusElement(focusableElements[currentIndex + 1], options.autoSelect);
  };

  const focusElement = (element: HTMLElement, autoSelect = true) => {
    if (element && element.offsetParent !== null) {
      element.focus();
      autoSelect && (element as HTMLInputElement).select?.();

      const field = findAttributeInParents(element, 'data-field', {
        stopAtClasses: ['ant-form-item'],
      });
      field && emit('focus-change', field);
    }
  };

  /** 根据表单字段名手动聚焦控件 */
  const focusByField = (field: string, options: FocusOptions = {}) => {
    const formEl = getFormEl();
    if (!formEl || !field) return;

    let targetElement: HTMLElement | null = null;
    const elements =
      formEl
        .querySelector(`.ant-form-item > div[data-field="${field}"]`)
        ?.querySelectorAll<
          HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >('input, textarea, select') || [];

    for (let i = 0; i < elements.length; i++) {
      const el = elements[i];
      if (!el.disabled && el.offsetParent !== null) {
        targetElement = el;
        break;
      }
    }
    if (!targetElement) return;

    nextTick(() => focusElement(targetElement, options.autoSelect));
  };

  const updateFocusFixedInput = () => {
    if (!inMtsApp) return;

    const formEl = getFormEl();
    if (!formEl || getProps.value.readonlyMode) return;

    if (formEl.offsetHeight <= 0) {
      onElementVisible(formEl, updateFocusFixedInput);
      return;
    }

    const elements = formEl.querySelectorAll<HTMLElement>(
      `.ant-form-item .mts-input-focused-fixed`,
    );
    for (const el of elements) {
      if (!el.className.includes('disabled') && !el.className.includes('readonly')) {
        el.style.maxWidth = el.offsetWidth + 'px';
      }
    }
  };

  return { handleKeyboardEnter, focusByField, updateFocusFixedInput };
}

const filterUnFocusableElements = (elements: HTMLElement[]) => {
  const filtered: HTMLElement[] = [];

  const len = elements.length;
  for (let i = 0; i < len; i++) {
    const el = elements[i] as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
    if (el.disabled || el.offsetParent === null) continue;

    !isElementHidden(el, {
      classNames: ['ant-form-item-hidden', 'ant-input-group-addon'],
      stopAtClasses: ['ant-form-item'],
    }) && filtered.push(el);
  }
  return filtered;
};
